import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

interface MonthlyDataItem {
  year: number;
  month: number;
  [key: string]: any;
}

interface YearlyDataItem {
  year: number;
  [key: string]: any;
}

@Component
export default class KpiCard extends Vue {
  @Prop({ required: true, type: String }) readonly label!: string;
  @Prop({ required: false, type: Number, default: null }) readonly value!: number | null;
  @Prop({ required: false, type: Array, default: null }) readonly monthlyData!: MonthlyDataItem[] | null;
  @Prop({ required: false, type: Array, default: null }) readonly yearlyData!: YearlyDataItem[] | null;
  @Prop({ required: false, type: String, default: null }) readonly valueKey!: string | null;
  @Prop({ required: false, type: Number, default: null }) readonly yoyChange!: number | null;
  @Prop({ required: false, type: String, default: null }) readonly currencyCode!: string | null;
  @Prop({ required: true, type: String }) readonly formatType!: 'currency' | 'integer' | 'percentage' | 'days';
  @Prop({ required: false, type: Boolean, default: false }) readonly invertColors!: boolean;
  @Prop({ required: false, type: Object, default: null }) readonly formatter!: any;
  @Prop({ required: false, type: String, default: null }) readonly stepperMode!: 'month' | 'year' | null;
  @Prop({ required: false, type: Date, default: null }) readonly currentPeriod!: Date | null;
  @Prop({ required: false, type: Boolean, default: false }) readonly loading!: boolean;

  public currentMonthIndex = 0;
  public currentYearIndex = 0;

  created() {
    // Skip initialization if parent controls the period
    if (this.currentPeriod) {
      return;
    }

    if (this.isMonthlyMode) {
      this.initializeToCurrentMonth();
    }
    if (this.isYearlyMode) {
      this.initializeToCurrentYear();
    }
  }

  @Watch('monthlyData')
  onMonthlyDataChanged() {
    // Skip initialization if parent controls the period
    if (this.currentPeriod) {
      return;
    }

    if (this.isMonthlyMode) {
      this.initializeToCurrentMonth();
    }
  }

  @Watch('yearlyData')
  onYearlyDataChanged() {
    // Skip initialization if parent controls the period
    if (this.currentPeriod) {
      return;
    }

    if (this.isYearlyMode) {
      this.initializeToCurrentYear();
    }
  }

  get isMonthlyMode(): boolean {
    return this.stepperMode === 'month' && this.monthlyData !== null && this.monthlyData !== undefined && Array.isArray(this.monthlyData);
  }

  get isYearlyMode(): boolean {
    return this.stepperMode === 'year' && this.yearlyData !== null && this.yearlyData !== undefined && Array.isArray(this.yearlyData);
  }

  get isStepperMode(): boolean {
    return this.isMonthlyMode || this.isYearlyMode;
  }

  get currentMonthData(): MonthlyDataItem | null {
    if (!this.isMonthlyMode || !this.monthlyData || this.monthlyData.length === 0) {
      return null;
    }

    // If parent provides currentPeriod, use it to find the data
    if (this.currentPeriod) {
      const targetYear = this.currentPeriod.getFullYear();
      const targetMonth = this.currentPeriod.getMonth() + 1;
      return this.monthlyData.find(item => item.year === targetYear && item.month === targetMonth) || null;
    }

    return this.monthlyData[this.currentMonthIndex] || null;
  }

  get currentYearData(): YearlyDataItem | null {
    if (!this.isYearlyMode || !this.yearlyData || this.yearlyData.length === 0) {
      return null;
    }

    // If parent provides currentPeriod, use it to find the data
    if (this.currentPeriod) {
      const targetYear = this.currentPeriod.getFullYear();
      return this.yearlyData.find(item => item.year === targetYear) || null;
    }

    return this.yearlyData[this.currentYearIndex] || null;
  }

  get previousYearMonthData(): MonthlyDataItem | null {
    if (!this.currentMonthData || !this.monthlyData) {
      return null;
    }

    const currentYear = this.currentMonthData.year;
    const currentMonth = this.currentMonthData.month;
    const previousYear = currentYear - 1;

    return this.monthlyData.find(item => item.year === previousYear && item.month === currentMonth) || null;
  }

  get previousYearData(): YearlyDataItem | null {
    if (!this.currentYearData || !this.yearlyData) {
      return null;
    }

    const currentYear = this.currentYearData.year;
    const previousYear = currentYear - 1;

    return this.yearlyData.find(item => item.year === previousYear) || null;
  }

  get displayValue(): number | null {
    if (this.isMonthlyMode) {
      if (!this.currentMonthData || !this.valueKey) {
        return null;
      }
      return this.currentMonthData[this.valueKey] ?? null;
    } else if (this.isYearlyMode) {
      if (!this.currentYearData || !this.valueKey) {
        return null;
      }
      return this.currentYearData[this.valueKey] ?? null;
    } else {
      return this.value;
    }
  }

  get calculatedYoyChange(): number | null {
    if (this.isMonthlyMode) {
      if (!this.currentMonthData || !this.previousYearMonthData || !this.valueKey) {
        return null;
      }

      const currentValue = this.currentMonthData[this.valueKey];
      const previousValue = this.previousYearMonthData[this.valueKey];

      if (
        currentValue === null ||
        currentValue === undefined ||
        previousValue === null ||
        previousValue === undefined ||
        previousValue === 0
      ) {
        return null;
      }

      return ((currentValue - previousValue) / previousValue) * 100;
    } else if (this.isYearlyMode) {
      if (!this.currentYearData || !this.previousYearData || !this.valueKey) {
        return null;
      }

      const currentValue = this.currentYearData[this.valueKey];
      const previousValue = this.previousYearData[this.valueKey];

      if (
        currentValue === null ||
        currentValue === undefined ||
        previousValue === null ||
        previousValue === undefined ||
        previousValue === 0
      ) {
        return null;
      }

      return ((currentValue - previousValue) / previousValue) * 100;
    } else {
      return this.yoyChange;
    }
  }

  get formattedValue(): string {
    const val = this.displayValue;

    if (val === null || val === undefined) {
      return '-';
    }

    switch (this.formatType) {
      case 'currency':
      case 'integer':
        return this.formatter ? this.formatter.toLocaleInteger(Math.round(val)) : String(Math.round(val));
      case 'percentage':
        return this.formatter ? this.formatter.toLocalePercentage(val, 1) : `${val.toFixed(1)}%`;
      case 'days':
        return val.toFixed(1);
      default:
        return String(val);
    }
  }

  get changeColorClass(): string {
    const change = this.calculatedYoyChange;
    if (change === null) {
      return '';
    }

    if (this.invertColors) {
      return change < 0 ? 'positive' : 'negative';
    } else {
      return change >= 0 ? 'positive' : 'negative';
    }
  }

  get changeIconColor(): string {
    const change = this.calculatedYoyChange;
    if (change === null) {
      return '';
    }

    if (this.invertColors) {
      return change < 0 ? 'success' : 'error';
    } else {
      return change >= 0 ? 'success' : 'error';
    }
  }

  get changeIcon(): string {
    const change = this.calculatedYoyChange;
    if (change === null) {
      return '';
    }

    if (this.invertColors) {
      return change < 0 ? 'mdi-arrow-down' : 'mdi-arrow-up';
    } else {
      return change >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down';
    }
  }

  get canGoPrevious(): boolean {
    // When parent controls the period, always allow going back
    if (this.currentPeriod) {
      return true;
    }

    if (this.isMonthlyMode) {
      return this.currentMonthIndex > 0;
    } else if (this.isYearlyMode) {
      return this.currentYearIndex > 0;
    }
    return false;
  }

  get canGoNext(): boolean {
    // When parent controls the period, check if we can go forward to current month/year
    if (this.currentPeriod) {
      const now = new Date();
      if (this.isMonthlyMode) {
        const currentYear = this.currentPeriod.getFullYear();
        const currentMonth = this.currentPeriod.getMonth();
        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth();
        // Can go next if current period is before now
        return currentYear < nowYear || (currentYear === nowYear && currentMonth < nowMonth);
      } else if (this.isYearlyMode) {
        const currentYear = this.currentPeriod.getFullYear();
        const nowYear = now.getFullYear();
        // Can go next if current year is before now
        return currentYear < nowYear;
      }
    }

    if (this.isMonthlyMode) {
      return this.monthlyData !== null && this.currentMonthIndex < this.monthlyData.length - 1;
    } else if (this.isYearlyMode) {
      return this.yearlyData !== null && this.currentYearIndex < this.yearlyData.length - 1;
    }
    return false;
  }

  get periodDisplay(): string {
    if (this.isMonthlyMode && this.currentMonthData) {
      const monthNames = [
        this.$t('rafaleApp.spe.dashboard.months.short.january'),
        this.$t('rafaleApp.spe.dashboard.months.short.february'),
        this.$t('rafaleApp.spe.dashboard.months.short.march'),
        this.$t('rafaleApp.spe.dashboard.months.short.april'),
        this.$t('rafaleApp.spe.dashboard.months.short.may'),
        this.$t('rafaleApp.spe.dashboard.months.short.june'),
        this.$t('rafaleApp.spe.dashboard.months.short.july'),
        this.$t('rafaleApp.spe.dashboard.months.short.august'),
        this.$t('rafaleApp.spe.dashboard.months.short.september'),
        this.$t('rafaleApp.spe.dashboard.months.short.october'),
        this.$t('rafaleApp.spe.dashboard.months.short.november'),
        this.$t('rafaleApp.spe.dashboard.months.short.december'),
      ];
      const monthName = monthNames[this.currentMonthData.month - 1];
      return `${monthName} ${this.currentMonthData.year}`;
    } else if (this.isYearlyMode && this.currentYearData) {
      return `${this.currentYearData.year}`;
    }
    return '-';
  }

  get currentMonthDisplay(): string {
    if (!this.currentMonthData) {
      return '-';
    }

    const monthNames = [
      this.$t('rafaleApp.spe.dashboard.months.january'),
      this.$t('rafaleApp.spe.dashboard.months.february'),
      this.$t('rafaleApp.spe.dashboard.months.march'),
      this.$t('rafaleApp.spe.dashboard.months.april'),
      this.$t('rafaleApp.spe.dashboard.months.may'),
      this.$t('rafaleApp.spe.dashboard.months.june'),
      this.$t('rafaleApp.spe.dashboard.months.july'),
      this.$t('rafaleApp.spe.dashboard.months.august'),
      this.$t('rafaleApp.spe.dashboard.months.september'),
      this.$t('rafaleApp.spe.dashboard.months.october'),
      this.$t('rafaleApp.spe.dashboard.months.november'),
      this.$t('rafaleApp.spe.dashboard.months.december'),
    ];

    const monthName = monthNames[this.currentMonthData.month - 1];
    return `${monthName} ${this.currentMonthData.year}`;
  }

  get previousMonthTooltip(): string {
    if (!this.canGoPrevious || !this.monthlyData) {
      return '';
    }

    const previousData = this.monthlyData[this.currentMonthIndex - 1];
    if (!previousData) {
      return '';
    }

    const monthNames = [
      this.$t('rafaleApp.spe.dashboard.months.january'),
      this.$t('rafaleApp.spe.dashboard.months.february'),
      this.$t('rafaleApp.spe.dashboard.months.march'),
      this.$t('rafaleApp.spe.dashboard.months.april'),
      this.$t('rafaleApp.spe.dashboard.months.may'),
      this.$t('rafaleApp.spe.dashboard.months.june'),
      this.$t('rafaleApp.spe.dashboard.months.july'),
      this.$t('rafaleApp.spe.dashboard.months.august'),
      this.$t('rafaleApp.spe.dashboard.months.september'),
      this.$t('rafaleApp.spe.dashboard.months.october'),
      this.$t('rafaleApp.spe.dashboard.months.november'),
      this.$t('rafaleApp.spe.dashboard.months.december'),
    ];

    const monthName = monthNames[previousData.month - 1];
    return `${monthName} ${previousData.year}`;
  }

  get nextMonthTooltip(): string {
    if (!this.canGoNext || !this.monthlyData) {
      return '';
    }

    const nextData = this.monthlyData[this.currentMonthIndex + 1];
    if (!nextData) {
      return '';
    }

    const monthNames = [
      this.$t('rafaleApp.spe.dashboard.months.january'),
      this.$t('rafaleApp.spe.dashboard.months.february'),
      this.$t('rafaleApp.spe.dashboard.months.march'),
      this.$t('rafaleApp.spe.dashboard.months.april'),
      this.$t('rafaleApp.spe.dashboard.months.may'),
      this.$t('rafaleApp.spe.dashboard.months.june'),
      this.$t('rafaleApp.spe.dashboard.months.july'),
      this.$t('rafaleApp.spe.dashboard.months.august'),
      this.$t('rafaleApp.spe.dashboard.months.september'),
      this.$t('rafaleApp.spe.dashboard.months.october'),
      this.$t('rafaleApp.spe.dashboard.months.november'),
      this.$t('rafaleApp.spe.dashboard.months.december'),
    ];

    const monthName = monthNames[nextData.month - 1];
    return `${monthName} ${nextData.year}`;
  }

  previousMonth(): void {
    if (this.canGoPrevious) {
      // If parent controls period, just emit event
      if (this.currentPeriod) {
        this.$emit('previous');
      } else {
        this.currentMonthIndex--;
        this.$emit('previous');
      }
    }
  }

  nextMonth(): void {
    if (this.canGoNext) {
      // If parent controls period, just emit event
      if (this.currentPeriod) {
        this.$emit('next');
      } else {
        this.currentMonthIndex++;
        this.$emit('next');
      }
    }
  }

  previousYear(): void {
    if (this.canGoPrevious) {
      // If parent controls period, just emit event
      if (this.currentPeriod) {
        this.$emit('previous');
      } else {
        this.currentYearIndex--;
        this.$emit('previous');
      }
    }
  }

  nextYear(): void {
    if (this.canGoNext) {
      // If parent controls period, just emit event
      if (this.currentPeriod) {
        this.$emit('next');
      } else {
        this.currentYearIndex++;
        this.$emit('next');
      }
    }
  }

  previousPeriod(): void {
    if (this.isMonthlyMode) {
      this.previousMonth();
    } else if (this.isYearlyMode) {
      this.previousYear();
    }
  }

  nextPeriod(): void {
    if (this.isMonthlyMode) {
      this.nextMonth();
    } else if (this.isYearlyMode) {
      this.nextYear();
    }
  }

  initializeToCurrentMonth(): void {
    if (!this.monthlyData || this.monthlyData.length === 0) {
      this.currentMonthIndex = 0;
      return;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

    // Try to find the current month in the data
    const currentMonthDataIndex = this.monthlyData.findIndex(item => item.year === currentYear && item.month === currentMonth);

    if (currentMonthDataIndex !== -1) {
      this.currentMonthIndex = currentMonthDataIndex;
    } else {
      // If current month not found, use the last available month
      this.currentMonthIndex = this.monthlyData.length - 1;
    }
  }

  initializeToCurrentYear(): void {
    if (!this.yearlyData || this.yearlyData.length === 0) {
      this.currentYearIndex = 0;
      return;
    }

    const now = new Date();
    const currentYear = now.getFullYear();

    // Try to find the current year in the data
    const currentYearDataIndex = this.yearlyData.findIndex(item => item.year === currentYear);

    if (currentYearDataIndex !== -1) {
      this.currentYearIndex = currentYearDataIndex;
    } else {
      // If current year not found, use the last available year
      this.currentYearIndex = this.yearlyData.length - 1;
    }
  }
}
